/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { getSupabaseClient } from '../lib/supabaseClient';
import { useDevMode } from '@lykbl/dev-tools';

export type UserRole = 'creator' | 'developer' | 'brand' | 'admin' | null;

interface AuthContextValue {
  user: User | null;
  session: Session | null;
  loading: boolean;
  isAuthenticated: boolean;
  role: UserRole;
  signOut: () => Promise<void>;
  refreshSession: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isDevMode, mockUser, mockSession } = useDevMode();

  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [role, setRole] = useState<UserRole>(null);

  // Extract role from user metadata or dev mode
  const extractRole = useCallback((userData: User | null): UserRole => {
    if (!userData) return null;
    
    // Check user metadata for role
    const metaRole = userData.user_metadata?.role || userData.app_metadata?.role;
    if (metaRole && ['creator', 'developer', 'brand', 'admin'].includes(metaRole)) {
      return metaRole as UserRole;
    }
    
    return null;
  }, []);

  // Dev mode: trust mock session
  useEffect(() => {
    if (isDevMode && mockUser && mockSession) {
      setUser(mockUser);
      setSession(mockSession);
      setRole(extractRole(mockUser) || (mockUser.user_metadata?.role as UserRole) || 'creator');
      setLoading(false);
      return;
    }
  }, [isDevMode, mockUser, mockSession, extractRole]);

  // Real Supabase auth lifecycle
  useEffect(() => {
    if (isDevMode) return;

    let cancelled = false;
    let authSubscription: { unsubscribe: () => void } | null = null;

    const bootstrap = async () => {
      try {
        const supabase = getSupabaseClient();

        const { data, error } = await supabase.auth.getSession();
        if (cancelled) return;
        if (error) throw error;
        setSession(data.session ?? null);
        setUser(data.session?.user ?? null);
        setRole(extractRole(data.session?.user ?? null));
      } catch (err) {
        console.warn('[marketing:auth] Supabase not available or auth failed:', err);
        setSession(null);
        setUser(null);
        setRole(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    const setupAuthListener = async () => {
      try {
        const supabase = getSupabaseClient();
        const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, nextSession) => {
          if (cancelled) return;
          setSession(nextSession);
          setUser(nextSession?.user ?? null);
          setRole(extractRole(nextSession?.user ?? null));
          setLoading(false);
        });
        authSubscription = subscription;
      } catch (err) {
        console.warn('[marketing:auth] Could not set up auth listener:', err);
      }
    };

    bootstrap();
    setupAuthListener();

    return () => {
      cancelled = true;
      if (authSubscription) {
        authSubscription.unsubscribe();
      }
    };
  }, [isDevMode, extractRole]);

  const signOut = useCallback(async () => {
    if (isDevMode) {
      setUser(null);
      setSession(null);
      setRole(null);
      return;
    }

    try {
      const supabase = getSupabaseClient();
      await supabase.auth.signOut();
    } catch (err) {
      console.warn('[marketing:auth] Could not sign out:', err);
    }
    setUser(null);
    setSession(null);
    setRole(null);
  }, [isDevMode]);

  const refreshSession = useCallback(async () => {
    try {
      const supabase = getSupabaseClient();
      const { data } = await supabase.auth.getSession();
      setSession(data.session ?? null);
      setUser(data.session?.user ?? null);
      setRole(extractRole(data.session?.user ?? null));
    } catch (err) {
      console.warn('[marketing:auth] Could not refresh session:', err);
      setSession(null);
      setUser(null);
      setRole(null);
    }
  }, [extractRole]);

  const value: AuthContextValue = {
    user,
    session,
    loading,
    isAuthenticated: !!user,
    role,
    signOut,
    refreshSession,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
};

